// apps/pwa-pos/src/lib/format.ts
// Sprint 37 Fase 1 — Helpers de formato monetario LATAM.
// Reemplaza todos los `.toFixed(2)` de la UI del POS por `formatMoney()`
// que formatea con locale es-CO (miles `.`, decimales `,`) y prepende el
// código de moneda manualmente (ej: "COP 141.500,00").
//
// Nota: usamos `Intl.NumberFormat` sin `style:'currency'` porque queremos el
// código de moneda (COP/USD/VES) como prefijo, no el símbolo local ($/US$/Bs).

const FORMATTER_CACHE = new Map<string, Intl.NumberFormat>();

function getNumberFormatter(): Intl.NumberFormat {
  const cached = FORMATTER_CACHE.get('_num_');
  if (cached) {
    return cached;
  }
  const fmt = new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  FORMATTER_CACHE.set('_num_', fmt);
  return fmt;
}

function safeFormat(monto: number): string {
  if (!Number.isFinite(monto)) return '0,00';
  return getNumberFormatter().format(monto);
}

/**
 * Formatea un monto en una moneda específica con el locale es-CO.
 * Ej: `formatMoney(141500, 'COP')` → `"COP 141.500,00"`.
 *
 * El código de moneda se prepende literal — útil porque en LATAM el patrón
 * visual es siempre `CÓDIGO monto` independientemente de la moneda.
 */
export function formatMoney(monto: number, currency: string): string {
  return `${currency} ${safeFormat(monto)}`;
}

/**
 * Variante sin código de moneda — útil cuando la moneda ya está visible
 * en otro lugar del layout (ej: header del carrito).
 */
export function formatAmount(monto: number, currency?: string): string {
  const num = safeFormat(monto);
  return currency ? `${currency} ${num}` : num;
}

/**
 * Formatea una cadena de texto representando un monto monetario mientras el usuario escribe.
 * Mantiene separadores de miles con puntos y decimales con coma.
 * Permite que el usuario escriba la coma/punto de decimales sin perderla.
 */
export function formatMoneyInput(value: string): string {
  if (!value) return '';

  // 1. Si el último carácter es un punto, lo normalizamos a coma
  let normalized = value;
  if (normalized.endsWith('.')) {
    normalized = normalized.slice(0, -1) + ',';
  }

  // 2. Buscamos la última coma (nuestro separador de decimales)
  const lastCommaIndex = normalized.lastIndexOf(',');

  let integerPart = normalized;
  let decimalPart = '';
  let hasDecimal = false;

  if (lastCommaIndex !== -1) {
    integerPart = normalized.substring(0, lastCommaIndex);
    decimalPart = normalized.substring(lastCommaIndex + 1);
    hasDecimal = true;
  }

  // 3. Limpiamos todos los puntos y espacios del entero
  integerPart = integerPart.replace(/[.\s\u00A0\u202F]/g, '').replace(/\D/g, '');

  // 4. Limpiamos cualquier carácter no numérico de los decimales y limitamos a 2 dígitos
  decimalPart = decimalPart.replace(/\D/g, '').substring(0, 2);

  // 5. Formateamos la parte entera determinísticamente con puntos cada 3 dígitos
  let formattedInteger = integerPart;
  if (integerPart) {
    const cleanInt = integerPart.replace(/^0+(?=\d)/, '');
    formattedInteger = cleanInt.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  } else if (hasDecimal) {
    formattedInteger = '0';
  }

  // 6. Combinamos
  let result = formattedInteger;
  if (hasDecimal) {
    result += ',' + decimalPart;
  }
  return result;
}
